// Audit log viewer (PRD §11.17, §16.8 Этап 6.4).
//
// Журнал действий администраторов. Только чтение; запись — из бизнес-кода.
// Фильтры применяются с debounce 300мс, без отдельной кнопки «Применить».

import { useEffect, useMemo, useState } from "react";
import { humanizeApiError } from "../api/client";
import {
  type AuditLogEntry,
  type AuditLogMetaResponse,
  type AuditLogQuery,
  auditLogApi,
} from "../api/auditLog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Pagination } from "../components/ui/pagination";
import { Select } from "../components/ui/select";

const PAGE_SIZE = 50;
const FILTER_DEBOUNCE_MS = 300;

type Status =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

export function AuditLogPage() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [targetKindFilter, setTargetKindFilter] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [advanced, setAdvanced] = useState(false);

  const [meta, setMeta] = useState<AuditLogMetaResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    auditLogApi
      .meta()
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch(() => {
        // Без meta селекты будут пустыми — это ок, в этом случае админ
        // включит «Расширенный поиск» и заполнит руками.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Один общий ключ фильтров — debounce-таймер обновляет «applied»-копию
  // и тем самым запускает запрос.
  const filterKey = useMemo(
    () => `${actionFilter}|${targetKindFilter}|${actorEmail.trim().toLowerCase()}|${page}`,
    [actionFilter, targetKindFilter, actorEmail, page],
  );
  const [applied, setApplied] = useState(filterKey);
  useEffect(() => {
    const t = window.setTimeout(() => setApplied(filterKey), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [filterKey]);

  // Сбрасываем страницу на 0 при изменении фильтров (но не самой страницы).
  useEffect(() => {
    setPage(0);
  }, [actionFilter, targetKindFilter, actorEmail]);

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: "loading" });
    const query: AuditLogQuery = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (actionFilter) query.action = actionFilter;
    if (targetKindFilter) query.target_kind = targetKindFilter;
    if (actorEmail.trim()) query.actor_email = actorEmail.trim();
    auditLogApi
      .list(query)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setStatus({ kind: "ready" });
      })
      .catch((err) => {
        if (!cancelled) setStatus({ kind: "error", message: extractMessage(err) });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  function resetFilters() {
    setActionFilter("");
    setTargetKindFilter("");
    setActorEmail("");
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromIdx = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const toIdx = Math.min(total, (page + 1) * PAGE_SIZE);
  const hasFilters =
    actionFilter !== "" || targetKindFilter !== "" || actorEmail.trim() !== "";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Аудит действий</h1>
        <p className="text-sm text-muted-foreground mt-1">
          История изменений в admin API: правки настроек,
          отключение и продление отправок, удаление данных
          получателей, retention-проходы.
        </p>
      </header>

      <Card data-testid="audit-log-filters">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Фильтры</CardTitle>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            aria-expanded={advanced}
          >
            {advanced ? "Простой поиск" : "Расширенный поиск"}
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          {!advanced ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="audit-action">Действие</Label>
                <Select
                  id="audit-action"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                >
                  <option value="">Все действия</option>
                  {meta?.actions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="audit-target-kind">Объект</Label>
                <Select
                  id="audit-target-kind"
                  value={targetKindFilter}
                  onChange={(e) => setTargetKindFilter(e.target.value)}
                >
                  <option value="">Все объекты</option>
                  {meta?.target_kinds.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="audit-actor-email">Email актора</Label>
                <Input
                  id="audit-actor-email"
                  type="search"
                  placeholder="ivan@example.com"
                  value={actorEmail}
                  onChange={(e) => setActorEmail(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="audit-action-raw">Action (точное)</Label>
                <Input
                  id="audit-action-raw"
                  placeholder="например, privacy.erase_recipient"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="audit-target-kind-raw">Target kind (точное)</Label>
                <Input
                  id="audit-target-kind-raw"
                  placeholder="dispatch / recipient / events / …"
                  value={targetKindFilter}
                  onChange={(e) => setTargetKindFilter(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="audit-actor-email-raw">Email актора</Label>
                <Input
                  id="audit-actor-email-raw"
                  placeholder="ivan@example.com"
                  value={actorEmail}
                  onChange={(e) => setActorEmail(e.target.value)}
                />
              </div>
            </div>
          )}
          {hasFilters && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetFilters} size="sm">
                Сбросить фильтры
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="audit-log-list">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Записи</CardTitle>
          <span className="text-xs text-muted-foreground">
            {total === 0
              ? "пусто"
              : `${fromIdx}–${toIdx} из ${total}`}
          </span>
        </CardHeader>
        <CardContent>
          {status.kind === "loading" && (
            <p className="text-sm text-muted-foreground">Загрузка…</p>
          )}
          {status.kind === "error" && (
            <p className="text-sm text-destructive" role="alert">
              {status.message}
            </p>
          )}
          {status.kind === "ready" && items.length === 0 && (
            <EmptyState
              title={
                hasFilters
                  ? "Ничего не нашли по вашему запросу"
                  : "Здесь пока ничего нет"
              }
              hint={
                hasFilters
                  ? "Попробуйте сбросить фильтры."
                  : "Записи появятся, когда администраторы выполнят первые действия."
              }
            />
          )}
          {status.kind === "ready" && items.length > 0 && (
            <ul
              className="divide-y border rounded-md"
              data-testid="audit-log-items"
            >
              {items.map((item) => (
                <AuditLogRow key={item.id} item={item} />
              ))}
            </ul>
          )}

          <div className="mt-4">
            <Pagination
              page={page + 1}
              totalPages={totalPages}
              total={total}
              disabled={status.kind === "loading"}
              onPageChange={(next) => setPage(next - 1)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditLogRow({ item }: { item: AuditLogEntry }) {
  const ts = useMemo(() => {
    const d = new Date(item.created_at);
    return Number.isNaN(d.getTime())
      ? item.created_at
      : d.toLocaleString("ru-RU");
  }, [item.created_at]);

  const payload = item.payload
    ? JSON.stringify(item.payload, null, 2)
    : null;

  return (
    <li className="p-3 text-sm space-y-1">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">{ts}</span>
        <code className="font-mono px-2 py-0.5 rounded bg-muted text-xs">
          {item.action}
        </code>
        {item.target_kind && (
          <span className="text-xs text-muted-foreground">
            target:{" "}
            <code className="font-mono">{item.target_kind}</code>
            {item.target_id && (
              <>
                {" / "}
                <code className="font-mono">{item.target_id}</code>
              </>
            )}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          actor:{" "}
          <code className="font-mono">
            {item.actor_user_id ?? "system"}
          </code>
        </span>
      </div>
      {payload && (
        <pre className="font-mono text-xs bg-muted p-2 rounded whitespace-pre-wrap break-all">
          {payload}
        </pre>
      )}
    </li>
  );
}
